import { useState, useMemo, useCallback } from 'react';
import { useIonToast } from '@ionic/react';
import MediaRecorderEncoder from 'opus-media-recorder';
import * as Sentry from '@sentry/browser';
// eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved,import/extensions
import EncoderWorker from 'worker-loader!opus-media-recorder/encoderWorker.js';

import {
  BrowserMediaRecorder,
  getNativeRecorderMimeType,
  createNativeRecorderSetup,
  getOpusWorkerOptions,
  OPUS_WAV_MIME_TYPE,
} from '../util/voiceRecordingUtils';

const MediaRecorderState = {
  NOT_INITIALIZED: 'notInitialized',
  RECORDING: 'recording',
  ERROR: 'error',
  INACTIVE: 'inactive',
};

const ErrorMessage = {
  DEFAULT: 'There was a problem recording your voice message',
};

const useMediaRecorder = (onDataAvailable) => {
  const [state, setState] = useState(MediaRecorderState.NOT_INITIALIZED);

  const [webRecorder, setWebRecorder] = useState();

  const [present] = useIonToast();

  const showToast = useCallback((message) => {
    present({
      message,
      duration: 2000,
      color: 'warning',
    });
  }, [present]);

  const startRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const nativeMimeType = getNativeRecorderMimeType();

    if (nativeMimeType && BrowserMediaRecorder) {
      try {
        const recorder = createNativeRecorderSetup(stream, nativeMimeType, BrowserMediaRecorder, {
          onStop: (blob) => onDataAvailable({ data: blob, mimeType: blob.type || nativeMimeType }),
          onError: (err) => {
            setState(MediaRecorderState.ERROR);
            showToast(ErrorMessage.DEFAULT);
            stream.getTracks().forEach((track) => track.stop());
            Sentry.captureException(err, { extra: { context: 'Native MediaRecorder error' } });
          },
        });
        setWebRecorder(recorder);
        setState(MediaRecorderState.RECORDING);
        return;
      } catch (e) {
        Sentry.captureException(e, { extra: { context: 'Native MediaRecorder failed, falling back to opus' } });
      }
    }

    const encoderWorkerFactory = () => new EncoderWorker();
    const workerOptions = getOpusWorkerOptions(encoderWorkerFactory);
    const mimeType = OPUS_WAV_MIME_TYPE;
    const recorder = new MediaRecorderEncoder(stream, { mimeType }, workerOptions);
    const chunks = [];
    recorder.addEventListener('dataavailable', (audioData) => {
      if (audioData.data && audioData.data.size > 0) chunks.push(audioData.data);
    });
    recorder.addEventListener('stop', () => {
      if (chunks.length === 0) return;
      const finalBlob = new Blob(chunks, { type: mimeType });
      if (finalBlob.size > 0) onDataAvailable({ data: finalBlob, mimeType: finalBlob.type || mimeType });
    });
    recorder.addEventListener('error', (error) => {
      setState(MediaRecorderState.ERROR);
      showToast(ErrorMessage.DEFAULT);
      stream.getTracks().forEach((track) => track.stop());
      Sentry.captureException(error, { extra: { context: 'Opus MediaRecorder error' } });
    });
    recorder.start();
    setWebRecorder(recorder);
    setState(MediaRecorderState.RECORDING);
  }, [
    onDataAvailable,
    showToast,
  ]);

  const stopRecording = useCallback(async () => {
    if (!webRecorder) return;
    webRecorder.stop();
    setState(MediaRecorderState.INACTIVE);
  }, [
    webRecorder,
  ]);

  return useMemo(() => ({
    isRecording: state === MediaRecorderState.RECORDING,
    startRecording,
    stopRecording,
  }), [
    state,
    startRecording,
    stopRecording,
  ]);
};

export default useMediaRecorder;
